import { computed, ref } from 'vue';
import { useReport } from '@/app/dashboards/composables/useReport';
import { useReportErrors } from '@/app/dashboards/composables/useReportErrors';
import { bothRangesInCurrentYear } from '@/utils/dateUtils';
import { abbrLargeNumber, formatDateRangeLabel } from '@/utils/formatters';

import { MEDIA_TYPES } from '@/models/dashboards/media-types.enum';
import {
  REPORT_MEDIA_TYPE_LABEL_MAP,
  ORGANIC_TABLE_COLUMNS,
  REPORT_ERROR_ICONS,
  REPORT_ERROR_TOOLTIPS,
  TIME_PERIOD_MESSAGES,
  REPORT_ERROR_VARIANTS,
  REPORT_ERROR_MESSAGES,
} from '@/app/dashboards/constants';
import customMetricsConstants from '@/app/settings/components/CustomMetrics/constants';
import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { useContentTagsStore } from '@/stores/content-tags';
import { REPORTS, isContentReport } from '@/app/dashboards/utils/reports.enum';
import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { useGraphReportDetails } from '@/app/dashboards/composables/useGraphReportDetails';
import { timeScaleTypes } from '@/config';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import {
  ADS_REPORT_AGGREGATE_BY_TYPES,
  REPORT_PREVIEW_AGGREGATE_BY_TYPES,
  DISPLAY_STRATEGY_OPTIONS,
} from '@/models/dashboards/aggregate-type.enum';
import {
  getChannelText,
  CHANNELS,
  TSI_ENABLED_CHANNELS,
  compareChannels,
} from '@/models/dashboards/channels.enum';
import {
  getBreakdownLabelWithFallback,
  getDashboardChannelIconName,
  getGoalChipProps,
  getSortOrderText,
} from '@/app/dashboards/utils/report.utils';
import capitalize from 'lodash/capitalize';
import { useContentReportDetails } from '@/app/dashboards/composables/useContentReportDetails';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useGalleryStore } from '@/stores/gallery';
import { useCampaignsStore } from '@/stores/campaigns';
import { useRoute } from 'vue-router';
import { useMetricFormatting } from '@/app/dashboards/composables/useMetricFormattingSetup';

import { useDashboardPermissionsStore } from '@/stores/dashboard-permissions';
import {
  REPORT_COMMUNITY_FILTERS,
  DISPLAY_STRATEGY,
  REPORT_LISTENING_FILTERS,
  BREAKDOWN_TYPE,
} from '@/app/dashboards/reportConstants';
import { useFilterOptions } from '@/app/dashboards/composables/useFilterOptions';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';

import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { Formatters } from '@dashhudson/dashing-ui';
import { useDashboardsSocialListeningStore } from '@/stores/dashboards-social-listening';
import { MESSAGE_TYPE_FILTER_OPTIONS } from '@/app/community/constants/communityFilters';
import {
  MEDIA_TYPE_FILTER_OPTIONS,
  MENTION_TYPE_FILTER_OPTIONS,
} from '@/app/socialListening/constants/listeningFilters';
import { isArray } from 'lodash-es';

export function useReportDetails({
  report,
  graphScale = ref(timeScaleTypes.DAILY.value),
  loading,
  preview = false,
  visibleDatasetIndexes = null,
  serverErrorMessage = ref(null),
} = {}) {
  const {
    availableAssignees,
    reportId,
    aggregateBy,
    secondaryAggregateBy,
    firstMetric,
    isCustomMetricReportTile,
    isUGCMetric,
    isUGCChannel,
    metricReportRows,
    metricTypeReportKey,
    formattedReportDataForTableAndGraphReports,
    currency,
    displayStrategy,
    reportBrands,
    reportBrandsMetrics,
    reportCategory,
    reportChannel,
    reportChannels,
    reportBrandIds,
    reportCompetitorsMetrics,
    reportCreatorMetrics,
    reportMediaTypeMetrics,
    reportMeta,
    reportMetricDetails,
    reportType,
    reportFilters,
    reportSortOrder,
    showGoalChip,
    topicComparisonFilterIds,
    topicFilterIds,
    boardFilterIds,
    campaignFilterIds,
    visionAiReport,
  } = useReport({
    report,
    loading,
    preview: ref(false),
  });

  const { isMultiMediaTypeReport, isTableReport, isLineChart, isBarChart, isHorizontalBarChart } =
    useReportType({
      reportType,
    });

  const {
    disconnectedChannels,
    brandsUserCannotAccess,
    brandsWithoutMetricAccess,
    brandsWithoutContentTagAccess,
    brandsWithoutCustomMetricAccess,
    hasErrors,
    hasNoData,
    errorMessageForDisplay,
    getChannelIconName,
    brandsWithoutCompetitiveAccess,
    brandsWithoutAdsAccess,
    brandsWithoutDashboardsAccess,
    brandsWithoutBenchmarkAccess,
    brandsWithoutCommunityAccess,
    competitorsWithoutAccess,
    creatorsWithoutAccess,
    reportRequiresAdAccountButDisconnected,
    brandsWithoutRequiredAdAccount,
  } = useReportErrors({
    report,
    loading,
  });

  const { canAccessListeningDashboards, extractUniqueChannelIconNames } = useDashboards();

  const dashboardPermissionsStore = useDashboardPermissionsStore();
  const route = useRoute();
  const communityMultiBrandsStore = useCommunityMultiBrandsStore();
  const messageTagsStore = useCommunityMessageTagsStore();

  const {
    deprecated,
    formula,
    postType,
    timeframe,
    dataType,
    isNegativeMetric,
    connectedAccountsOnly,
    targetMetricDetails,
  } = useMetricDetails({
    metric: firstMetric,
    channels: reportChannels,
    metricReportTypeKey: metricTypeReportKey,
  });

  const {
    chartOptions,
    chartFormatConfig,
    generateCustomTooltip,
    goalValues,
    prioritizedGraphReportData,
    graphType,
    isStackedBarChart,
    showLegend,
  } = useGraphReportDetails({
    report,
    graphScale,
  });

  const { formattedMediaItems } = useContentReportDetails({
    report,
    loading,
  });

  const { getCustomReportDetailsFromId } = useCustomMetrics();

  const {
    isCreatorReport,
    isCompetitiveReport,
    isBenchmarkReport,
    isAdsReportType,
    isDoughnutChart,
  } = useReportType({
    reportType,
  });

  const { isCommunityReportCategory, isListeningReportCategory, isTSIReportCategory } =
    useReportCategory({
      reportCategory,
    });
  const { filtersToShow } = useFilterOptions({
    reportCategory,
    selectedChannels: reportChannels,
    selectedBrandIds: reportBrandIds,
  });

  const { reportDateRange } = useDashboardReportsStore();
  const { allOrganizationsContentTags } = useContentTagsStore();

  const galleryStore = useGalleryStore();
  const campaignsStore = useCampaignsStore();
  const dashboardsSocialListeningStore = useDashboardsSocialListeningStore();

  const customMetricDetails = computed(() => getCustomReportDetailsFromId(firstMetric.value));
  const { getFormatConfig, getComparisonFormatConfig } = useMetricFormatting({
    dataType,
    isCustomMetricReportTile,
    customMetricDetails,
    currency,
    displayStrategy,
  });
  const isPdfFormat = computed(() => !!route?.meta?.report);
  const isShared = computed(() => !!route?.meta?.shared);

  const customMetricName = computed(
    () => customMetricDetails?.value?.name ?? reportMeta.value.name,
  );

  const negativeMetric = computed(() => {
    if (customMetricDetails.value) {
      return (
        customMetricDetails.value.trendInterpretation ===
        customMetricsConstants.TREND_INTERPRETATION.DECLINE.value
      );
    }
    return isNegativeMetric.value;
  });

  const overrideReportDateRange = computed(() => {
    const staticDates = reportMeta.value?.static_dates;
    const adjustedDates = reportMeta.value?.adjusted_dates;
    const overrideStartDate = staticDates?.start_date || adjustedDates.start_date;
    const overrideEndDate = staticDates?.end_date || adjustedDates.end_date;
    if (!overrideStartDate || !overrideEndDate) return null;
    return [overrideStartDate, overrideEndDate];
  });

  const customDateRange = computed(() => {
    const staticDates = reportMeta.value?.static_dates;
    if (!staticDates?.start_date || !staticDates?.end_date) return null;
    return [staticDates.start_date, staticDates.end_date];
  });

  const adjustedReportingRange = computed(() => {
    const adjustedDates = reportMeta.value?.adjusted_dates;
    if (!adjustedDates?.start_date || !adjustedDates?.end_date) return null;
    return { start: adjustedDates.start_date, end: adjustedDates.end_date };
  });

  const adjustedComparisonRange = computed(() => {
    const adjustedDates = reportMeta.value?.adjusted_dates;
    if (!adjustedDates?.context_start_date || !adjustedDates?.context_end_date) return null;
    return { start: adjustedDates.context_start_date, end: adjustedDates.context_end_date };
  });

  const overrideReportDateRangeRollingTime = computed(
    () => reportMeta.value?.static_dates?.rolling_time,
  );

  const overrideContextDateRange = computed(() => {
    if (
      !reportMeta.value?.static_dates?.context_start_date ||
      !reportMeta.value?.static_dates?.context_end_date
    )
      return null;
    return [
      reportMeta.value.static_dates.context_start_date,
      reportMeta.value.static_dates.context_end_date,
    ];
  });

  const bothRangesInSameYear = computed(() =>
    bothRangesInCurrentYear(overrideReportDateRange.value, overrideContextDateRange.value),
  );

  const reportPeriodDisplay = computed(() => {
    const forceYearDisplay = !bothRangesInSameYear.value;
    return formatDateRangeLabel(
      overrideReportDateRange.value[0],
      overrideReportDateRange.value[1],
      {
        forceYearDisplay,
      },
    );
  });
  const contextPeriodDisplay = computed(() => {
    const forceYearDisplay = !bothRangesInSameYear.value;
    return formatDateRangeLabel(
      overrideContextDateRange.value[0],
      overrideContextDateRange.value[1],
      {
        forceYearDisplay,
      },
    );
  });

  const dateRangeDisplay = computed(() => {
    if (overrideReportDateRange.value && overrideContextDateRange.value) {
      return `${reportPeriodDisplay.value} vs ${contextPeriodDisplay.value}`;
    }

    return overrideReportDateRange.value ? reportPeriodDisplay.value : null;
  });

  function buildTitleWithPercentageSuffix({ title, metricName, aggregateByValue, isTable }) {
    const TOPIC_AGGREGATE_TYPE = 'TOPIC';
    const currentStrategy = displayStrategy.value;
    const { topicLabel, suffix } = DISPLAY_STRATEGY_OPTIONS[currentStrategy] || {};

    if (currentStrategy === DISPLAY_STRATEGY.PERCENT_OF_GRAND_TOTAL) {
      if (isTable && aggregateByValue === TOPIC_AGGREGATE_TYPE && topicLabel) {
        return `${topicLabel} ${metricName}`;
      }
      return `${title} ${suffix || ''}`;
    }

    return title;
  }

  function buildTitle({ baseTitle, metricName, aggregateByValue, isTable }) {
    let title = baseTitle;
    if (secondaryAggregateBy.value && secondaryAggregateBy.value !== 'NONE') {
      const formattedBreakdown =
        REPORT_PREVIEW_AGGREGATE_BY_TYPES?.[secondaryAggregateBy.value]?.formattedValue ||
        capitalize(secondaryAggregateBy.value);
      title = `${title} and ${formattedBreakdown}`;
    }
    return buildTitleWithPercentageSuffix({ title, metricName, aggregateByValue, isTable });
  }

  function getFormattedAggregateBy() {
    if (reportType.value === REPORTS.MULTI_METRIC_MEDIA_TYPE.value) {
      return 'by Media Type';
    }

    if (aggregateBy.value) {
      return (
        REPORT_PREVIEW_AGGREGATE_BY_TYPES?.[aggregateBy.value]?.label ||
        `by ${capitalize(aggregateBy.value)}`
      );
    }

    return '';
  }

  const metricTitle = computed(() => {
    if (isCustomMetricReportTile.value) return customMetricName.value;
    let metricName = reportMetricDetails.value?.displayName;

    if (isContentReport(reportType.value)) {
      return getSortOrderText(reportSortOrder.value, reportChannel.value, reportType, metricName);
    }

    if (!metricName && isMultiMediaTypeReport.value) {
      metricName = reportMetricDetails.value?.parentMetric;
    }

    if (!metricName) {
      return null;
    }

    if (!canAccessListeningDashboards.value) {
      return metricName;
    }

    const formattedAggregateBy = getFormattedAggregateBy();

    if (isTableReport.value) {
      return buildTitle({
        baseTitle: `${metricName} ${formattedAggregateBy}`,
        metricName,
        aggregateByValue: aggregateBy.value,
        isTable: true,
      });
    }

    if (isLineChart.value) {
      return buildTitle({
        baseTitle: `${metricName} over Time`,
        metricName,
        aggregateByValue: aggregateBy.value,
        isTable: false,
      });
    }

    if (isBarChart.value || isHorizontalBarChart.value) {
      return buildTitle({
        baseTitle: `${metricName} ${formattedAggregateBy}`,
        metricName,
        aggregateByValue: aggregateBy.value,
        isTable: false,
      });
    }

    return metricName;
  });

  const titleTooltip = computed(() => {
    if (isCustomMetricReportTile.value) {
      if (hasNoData.value) return null;

      return {
        title: customMetricName.value,
        content: customMetricDetails.value.reportTooltip.description,
        footer: customMetricDetails.value.reportTooltip.formula,
      };
    }

    return {
      title: metricTitle.value,
      content: reportMetricDetails.value.tooltip,
      footer: formula.value,
    };
  });

  const contentTagIds = computed(() => reportMeta.value?.content_tag_ids || []);

  const channels = computed(() =>
    extractUniqueChannelIconNames(
      reportChannels.value?.[0] === CHANNELS.TSI_CHANNELS.value
        ? TSI_ENABLED_CHANNELS
        : reportChannels.value,
    ).map((channel) => ({ channel })),
  );

  const sortedChannels = computed(() =>
    [...channels.value].sort((a, b) => compareChannels(a.channel, b.channel)),
  );

  const getBrandErrorIcons = ({ brandId, name }) => {
    if (isShared.value) {
      return [];
    }

    const cantAccessBrand = brandsUserCannotAccess.value[brandId];
    if (cantAccessBrand) {
      return [
        {
          name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
          tooltip: REPORT_ERROR_TOOLTIPS.NO_BRAND_ACCESS,
        },
      ];
    }
    const disconnectedChannelsList = disconnectedChannels.value[brandId] || [];
    const cantAccessCustomMetric = brandsWithoutCustomMetricAccess.value[brandId];
    const cantAccessMetric = brandsWithoutMetricAccess.value[brandId];
    const cantAccessCompetitive = brandsWithoutCompetitiveAccess.value[brandId];
    const inaccessibleContentTags = brandsWithoutContentTagAccess.value[brandId] || [];
    const cantAccessAds = brandsWithoutAdsAccess.value[brandId];
    const cantAccessBenchmark = brandsWithoutBenchmarkAccess.value[brandId];
    const cantAccessCommunity = brandsWithoutCommunityAccess.value[brandId];
    const cantAccessDashboards = brandsWithoutDashboardsAccess.value[brandId];

    const icons = [];
    const alertMessages = [];

    if (disconnectedChannelsList.length > 0) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.ACCOUNT_DISCONNECTED);

      disconnectedChannelsList.forEach((channel) => {
        icons.push({
          name: getChannelIconName(channel),
          tooltip: getChannelText(channel),
        });
      });
    }

    if (cantAccessDashboards) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_DASHBOARDS_ACCESS);
    }

    if (cantAccessMetric) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_METRIC_ACCESS);
    }

    if (cantAccessCustomMetric) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_CUSTOM_METRIC_ACCESS);
    }

    if (cantAccessCompetitive && isCompetitiveReport.value) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_COMPETITIVE_ACCESS);
    }

    if (cantAccessAds && isAdsReportType.value) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_ADS_ACCESS);

      if (Array.isArray(cantAccessAds)) {
        cantAccessAds.forEach((channel) => {
          icons.push({
            name: getChannelIconName(channel),
            tooltip: getChannelText(channel),
          });
        });
      }
    }

    const adAccountDisconnectedInfo = brandsWithoutRequiredAdAccount?.value?.[brandId];
    if (adAccountDisconnectedInfo && disconnectedChannelsList.length === 0) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.AD_ACCOUNT_DISCONNECTED);
      icons.push({
        name: getChannelIconName(adAccountDisconnectedInfo.channel),
        tooltip: getChannelText(adAccountDisconnectedInfo.channel),
      });
    }

    if (cantAccessBenchmark && isBenchmarkReport.value) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_BENCHMARK_ACCESS);
    }

    if (cantAccessCommunity && isCommunityReportCategory.value) {
      alertMessages.push(REPORT_ERROR_TOOLTIPS.NO_COMMUNITY_ACCESS);
    }

    if (alertMessages.length > 0) {
      icons.unshift({
        name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
        tooltip: alertMessages.join('\n'),
      });
    }

    if (inaccessibleContentTags.length > 0) {
      const tagsList = inaccessibleContentTags.join('", "');
      icons.push({
        name: REPORT_ERROR_ICONS.TAG,
        tooltip: REPORT_ERROR_TOOLTIPS.CONTENT_TAG_ACCESS(name, tagsList),
      });
    }

    return icons;
  };

  const contentTagNames = computed(() => {
    const contentTagIdsSet = new Set(contentTagIds.value);
    const foundContentTags = allOrganizationsContentTags.filter((contentTag) =>
      contentTagIdsSet.has(contentTag.id),
    );
    if (foundContentTags.length < 1) return null;
    return foundContentTags.map((foundContentTag) => foundContentTag.name);
  });

  const convertedMetricTimeFrame = computed(() => {
    if (!timeframe.value) return undefined;

    const timeframeMapping = {
      ACTIVITY_DURING_TIMEFRAME: 'activity-this-period',
      POSTS_PUBLISHED_IN_TIMEFRAME: 'posts-this-period',
    };

    return timeframeMapping[timeframe.value] || timeframe.value;
  });

  const messageFilterData = computed(() => {
    if (!isCommunityReportCategory.value || !reportFilters.value) {
      return undefined;
    }

    const filters = reportFilters.value;
    const filterOptionsData = filtersToShow.value;

    const messageTagIds = filters[REPORT_COMMUNITY_FILTERS.MESSAGE_TAG_IDS] || [];
    const filterInfo = filterOptionsData?.find(
      (info) => info.formKey === REPORT_COMMUNITY_FILTERS.MESSAGE_TAG_IDS,
    );

    const messageTags = messageTagIds.map((id) => {
      const foundOption = filterInfo?.options
        .flatMap((brandTags) => brandTags.children || [])
        .find((option) => option.value === id);

      if (foundOption) {
        return {
          label: foundOption.label,
          color: foundOption.color,
          badge: { color: foundOption.color },
        };
      }

      return {
        label: getBreakdownLabelWithFallback(id, BREAKDOWN_TYPE.MESSAGE_TAG),
      };
    });

    const messageTypes = filters[REPORT_COMMUNITY_FILTERS.MESSAGE_TYPES] || [];
    const sentiment = filters[REPORT_COMMUNITY_FILTERS.SENTIMENT] || [];
    const assigneeIds = filters[REPORT_COMMUNITY_FILTERS.ASSIGNEE_IDS] || [];

    const assigneeFilterInfo = filterOptionsData?.find(
      (info) => info.formKey === REPORT_COMMUNITY_FILTERS.ASSIGNEE_IDS,
    );

    const messageAssignees = assigneeIds.map((id) => {
      const assignee = assigneeFilterInfo?.options
        ?.flatMap((opt) => opt.children || [opt])
        .find((opt) => opt.value === id || opt.id === id);
      return assignee?.label || getBreakdownLabelWithFallback(id, BREAKDOWN_TYPE.ASSIGNEE);
    });

    return {
      messageTags,
      messageTypes,
      sentiment,
      messageAssignees,
    };
  });

  const getBrandAvatars = computed(() =>
    reportBrands.value.map((brand) => {
      const { id: brandId, name } = brand;

      if (brand.isCompetitor) {
        return {
          avatar: brand.avatar,
          label: brand.name ?? '',
          icons: null,
        };
      }

      const icons = !preview ? getBrandErrorIcons({ brandId, name }) : [];

      return {
        avatar: brand.avatar,
        label: brand.name ?? '',
        icons: icons.length > 0 ? icons : null,
      };
    }),
  );

  const boardNames = computed(() =>
    boardFilterIds.value.map((boardId) => {
      let board = galleryStore.galleries.find((gallery) => gallery.id === boardId);

      if (!board) {
        Object.values(galleryStore.galleriesMultiBrand).forEach((brandGalleries) => {
          if (!board) {
            board = brandGalleries.find((g) => g.id === boardId);
          }
        });
      }

      return board?.name || getBreakdownLabelWithFallback(boardId, BREAKDOWN_TYPE.BOARD);
    }),
  );

  const campaignNames = computed(() =>
    campaignFilterIds.value.map((campaignId) => {
      let campaign = campaignsStore.campaigns.find((c) => c.id === campaignId);

      if (!campaign) {
        Object.values(campaignsStore.campaignsMultiBrand).forEach((brandCampaigns) => {
          if (!campaign) {
            campaign = brandCampaigns.find((c) => c.id === campaignId);
          }
        });
      }

      return campaign?.name || getBreakdownLabelWithFallback(campaignId, BREAKDOWN_TYPE.CAMPAIGN);
    }),
  );

  const topicNameList = computed(() => {
    const allTopicIds = [...topicFilterIds.value, ...topicComparisonFilterIds.value];
    const allTopics = [
      ...dashboardsSocialListeningStore.topicListForMultipleOrganizations,
      ...dashboardsSocialListeningStore.comparisonListForMultipleOrganizations.flatMap(
        (comparison) => comparison.topics ?? [],
      ),
    ];

    return allTopics
      .filter((topic) => allTopicIds.includes(topic.id))
      .map((topic) => topic.name || getBreakdownLabelWithFallback(topic.id, BREAKDOWN_TYPE.TOPIC));
  });

  const getCreatorAvatars = computed(() =>
    reportCreatorMetrics.value.map((creator) => {
      let icons = creatorsWithoutAccess.value?.[creator.id]
        ? [
            {
              name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
              tooltip: REPORT_ERROR_TOOLTIPS.NO_CREATOR_ACCESS,
            },
          ]
        : null;
      if (!icons) {
        icons =
          creator.isCreator &&
          connectedAccountsOnly.value &&
          !dashboardPermissionsStore.creatorIsConnected(creator)
            ? [
                {
                  name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
                  tooltip: REPORT_ERROR_TOOLTIPS.DISCONNECTED_CREATOR_STATUS,
                },
              ]
            : null;
      }
      return {
        avatar: creator?.avatar,
        label: creator?.label ?? '',
        icons,
      };
    }),
  );

  const creatorReportChip = computed(() => {
    if (!isCreatorReport.value) return undefined;
    return {
      items: getCreatorAvatars.value,
      avatarSize: 'xs',
    };
  });

  const competitorLabels = computed(() => {
    const competitors = reportFilters.value?.[REPORT_LISTENING_FILTERS.COMPETITOR] || [];
    if (!competitors.length) return [];

    const competitorFilter = filtersToShow.value?.find(
      (info) => info.formKey === REPORT_LISTENING_FILTERS.COMPETITOR,
    );

    return competitors.map((id) => {
      const competitor = competitorFilter?.options
        ?.flatMap((opt) => opt.children || [opt])
        .find((opt) => opt.value === id || opt.id === id);
      return {
        label: competitor?.label || getBreakdownLabelWithFallback(id, BREAKDOWN_TYPE.COMPETITOR),
      };
    });
  });
  const locationLabels = computed(() => {
    const locations =
      reportFilters.value?.[REPORT_LISTENING_FILTERS.LOCATION] ??
      reportMeta.value?.listening?.filters?.locations ??
      [];

    if (!locations || locations.length === 0) {
      return undefined;
    }
    return locations.map((location) => {
      const parts = [];

      if (location.city) {
        parts.push(location.city);
      }
      if (location.administrative_division) {
        parts.push(location.administrative_division);
      }
      if (location.country) {
        parts.push(location.country);
      }
      return parts.length > 0 ? parts.join(', ') : undefined;
    });
  });

  const industryLabels = computed(() => {
    const industries = reportFilters.value?.[REPORT_LISTENING_FILTERS.INDUSTRY] || [];
    if (!industries.length) return [];

    const industryFilter = filtersToShow.value?.find(
      (info) => info.formKey === REPORT_LISTENING_FILTERS.INDUSTRY,
    );

    return industries.map((id) => {
      const industry = industryFilter?.options
        ?.flatMap((opt) => opt.children || [opt])
        .find((opt) => opt.value === id || opt.id === id);
      return industry?.label || getBreakdownLabelWithFallback(id, BREAKDOWN_TYPE.INDUSTRY);
    });
  });

  const messageTypeLabels = computed(() => {
    const messageTypes = messageFilterData.value?.messageTypes;
    if (!messageTypes || messageTypes.length === 0) {
      return undefined;
    }
    return messageTypes.map((type) => {
      const messageTypeOption = Object.values(MESSAGE_TYPE_FILTER_OPTIONS).find(
        (option) => option.statsValue === type,
      );
      return messageTypeOption ? messageTypeOption.label : type;
    });
  });

  const mediaTypeLabels = computed(() => {
    const mediaTypes = reportFilters.value?.[REPORT_LISTENING_FILTERS.MEDIA_TYPE] || [];
    if (!mediaTypes.length) return undefined;
    const mediaTypeFilter = filtersToShow.value?.find(
      (info) => info.formKey === REPORT_LISTENING_FILTERS.MEDIA_TYPE,
    );
    return mediaTypes.map((value) => {
      if (mediaTypeFilter) {
        const found = mediaTypeFilter.options.find((opt) => opt.value === value);
        return found ? found.label : value;
      }
      const option = Object.values(MEDIA_TYPE_FILTER_OPTIONS).find((opt) => opt.value === value);
      return option ? option.label : value;
    });
  });

  const mentionTypeLabels = computed(() => {
    const mentionTypes = reportFilters.value?.[REPORT_LISTENING_FILTERS.MENTION_TYPE] || [];
    if (!mentionTypes.length) return undefined;
    const mentionTypeFilter = filtersToShow.value?.find(
      (info) => info.formKey === REPORT_LISTENING_FILTERS.MENTION_TYPE,
    );
    return mentionTypes.map((value) => {
      if (mentionTypeFilter) {
        const found = mentionTypeFilter.options.find((opt) => opt.value === value);
        return found ? found.label : value;
      }
      const option = Object.values(MENTION_TYPE_FILTER_OPTIONS).find((opt) => opt.value === value);
      return option ? option.label : value;
    });
  });

  const sentimentLabels = computed(() => {
    if (isCommunityReportCategory.value && messageFilterData.value) {
      const communitySentiment = messageFilterData.value.sentiment;
      return communitySentiment;
    }
    const listeningSentiment = reportFilters.value?.[REPORT_LISTENING_FILTERS.SENTIMENT];
    if (listeningSentiment && listeningSentiment.length > 0) {
      return listeningSentiment;
    }
    return undefined;
  });

  const creatorLabels = computed(() =>
    creatorReportChip.value?.items?.map((item) => item.label).filter(Boolean),
  );

  const reportAttributes = computed(() => ({
    ugc: isUGCChannel.value && !isCreatorReport.value,
    creators: creatorLabels.value,
    boards: boardNames.value,
    campaigns: campaignNames.value,
    postTypes: postType.value ? [postType.value] : undefined,
    metricTimeFrame: convertedMetricTimeFrame.value,
    customMetric: isCustomMetricReportTile.value,
    deprecated: deprecated.value,
    customDateRange: customDateRange.value
      ? {
          start: customDateRange.value[0],
          end: customDateRange.value[1],
          ...(overrideReportDateRangeRollingTime.value && {
            rollingTime: overrideReportDateRangeRollingTime.value,
          }),
        }
      : null,
    adjustedReportingRange: adjustedReportingRange.value,
    adjustedComparisonRange: adjustedComparisonRange.value,
    contentTags: contentTagNames.value?.map((name) => ({ label: name })),
    sentiment: sentimentLabels.value,
    messageTags: messageFilterData.value?.messageTags,
    messageTypes: messageTypeLabels.value,
    messageStates: messageFilterData.value?.messageStates,
    messageAssignees: messageFilterData.value?.messageAssignees,
    topics: topicNameList.value,
    competitors: competitorLabels.value,
    industries: industryLabels.value,
    locations: locationLabels.value,
    mediaTypes: mediaTypeLabels.value,
    mentionTypes: mentionTypeLabels.value,
  }));

  const getCompetitorAvatars = computed(() =>
    reportCompetitorsMetrics.value.map((competitor) => {
      const competitorId = competitor.id;

      const icons = competitorsWithoutAccess.value[competitorId]
        ? [
            {
              name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
              tooltip: REPORT_ERROR_TOOLTIPS.NO_COMPETITOR_ACCESS,
            },
          ]
        : null;

      return {
        avatar: competitor.avatar,
        label: competitor.name ?? '',
        icons,
      };
    }),
  );

  const avatarGroups = computed(() => {
    const groups = [
      {
        label: 'Brands',
        avatars: getBrandAvatars.value,
      },
      getCompetitorAvatars.value.length
        ? {
            label: 'Competitors',
            avatars: getCompetitorAvatars.value,
          }
        : undefined,
    ].filter(Boolean);

    let maxPreviewDisplayItems = 4;
    if (isDoughnutChart.value) {
      maxPreviewDisplayItems = getBrandAvatars.value.length > 2 ? 0 : 2;
    }

    return {
      groups,
      maxDisplayItems: preview || visionAiReport.value ? maxPreviewDisplayItems : 12,
    };
  });

  const channelGroups = computed(() => {
    const channelsWithErrors = sortedChannels.value.map((channelObj) => {
      const channel = channelObj.channel;

      if (isShared.value) {
        return channelObj;
      }

      const brandsWithDisconnectedChannel = [];

      Object.entries(disconnectedChannels.value).forEach(([brandId, channelList]) => {
        if (channelList.includes(channel)) {
          const brand = reportBrands.value.find((b) => b.id.toString() === brandId.toString());
          if (brand) {
            brandsWithDisconnectedChannel.push(brand.name);
          }
        }
      });

      if (brandsWithDisconnectedChannel.length > 0 && !preview) {
        return {
          channel,
          icons: [
            {
              name: REPORT_ERROR_ICONS.ALERT_TRIANGLE,
              tooltip: REPORT_ERROR_TOOLTIPS.ACCOUNT_DISCONNECTED,
            },
          ],
          showAlert: true,
        };
      }

      return channelObj;
    });

    return {
      channels: channelsWithErrors,
    };
  });

  const showVideo = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return mediaType === MEDIA_TYPES.VIDEO.value || mediaType === MEDIA_TYPES.VIDEO_REEL.value;
    }),
  );

  const showReel = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return mediaType === MEDIA_TYPES.REEL.value || mediaType === MEDIA_TYPES.VIDEO_REEL.value;
    }),
  );

  const showPhoto = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return (
        mediaType === MEDIA_TYPES.PHOTO.value || mediaType === MEDIA_TYPES.PHOTO_CAROUSEL.value
      );
    }),
  );

  const showCarousel = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return (
        mediaType === MEDIA_TYPES.CAROUSEL.value || mediaType === MEDIA_TYPES.PHOTO_CAROUSEL.value
      );
    }),
  );

  const showLink = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return mediaType === MEDIA_TYPES.LINK.value;
    }),
  );

  const showStatus = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return mediaType === MEDIA_TYPES.STATUS.value;
    }),
  );

  const showAllMedia = computed(() =>
    reportMediaTypeMetrics.value.some((reportMediaTypeMetric) => {
      const mediaType = reportMediaTypeMetric?.mediaType?.value;
      return (
        mediaType === MEDIA_TYPES.ALL_MEDIA.value ||
        reportMediaTypeMetric?.mediaType?.metric.startsWith('UGC_')
      );
    }),
  );

  const mediaTypeTooltips = computed(() => {
    const tooltips = [];
    if (showAllMedia.value) tooltips.push(MEDIA_TYPES.ALL_MEDIA.tooltip);
    if (showVideo.value) tooltips.push(MEDIA_TYPES.VIDEO.tooltip);
    if (showReel.value) tooltips.push(MEDIA_TYPES.REEL.tooltip);
    if (showPhoto.value) tooltips.push(MEDIA_TYPES.PHOTO.tooltip);
    if (showCarousel.value) tooltips.push(MEDIA_TYPES.CAROUSEL.tooltip);
    if (showLink.value) tooltips.push(MEDIA_TYPES.LINK.tooltip);
    if (showStatus.value) tooltips.push(MEDIA_TYPES.STATUS.tooltip);
    return tooltips.join(', ');
  });

  const mediaTypeIcons = computed(() => {
    const icons = [];
    if (showAllMedia.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.ALL_MEDIA);
    if (showVideo.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.VIDEO);
    if (showReel.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.REEL);
    if (showPhoto.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.PHOTO);
    if (showCarousel.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.CAROUSEL);
    if (showLink.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.LINK);
    if (showStatus.value) icons.push(REPORT_MEDIA_TYPE_LABEL_MAP.STATUS);

    if (icons.length > 1) {
      return {
        icon: icons[0],
        iconRight: icons[1],
      };
    }
    return {
      icon: icons[0],
    };
  });

  const reportHeaderProps = computed(() => ({
    avatarGroups: avatarGroups.value,
    channelGroups: channelGroups.value,
    metricTitle: metricTitle.value,
    metricTooltip: preview ? null : titleTooltip.value,
    reportAttributes: reportAttributes.value,
    isPublicData: isListeningReportCategory.value && !isContentReport(reportType.value),
  }));

  const isAdsTotalGroupedMetricReport = computed(
    () => reportType.value === REPORTS.ADS_TOTAL_GROUPED_METRIC.value,
  );

  /**
   * Extracts metric data from a row, handling both structures:
   * 1. Row with audiences: returns arrays of values from all audiences
   * 2. Row with direct metric: returns single metric values
   *
   * @param {Object} row - The row object that may have audiences or direct metric
   * @returns {Object} - Object with context, context_change, and value (single values or arrays)
   */
  function extractMetricData(row) {
    if (row?.audiences) {
      // Row has audiences structure - return arrays
      const audiences = Object.values(row.audiences);
      return {
        comparisonPeriod: audiences.map((audience) => audience.metric?.context),
        change: audiences.map((audience) => audience.metric?.context_change),
        reportingPeriod: audiences.map((audience) => audience.metric?.value),
        percentageToGoal: audiences.map((audience) => audience.metric?.percent_of_goal),
      };
    }

    // Row has direct metric structure - return single values
    return {
      comparisonPeriod: row.metric?.context,
      change: row.metric?.context_change,
      reportingPeriod: row.metric?.value,
      percentageToGoal: row.metric?.percent_of_goal,
    };
  }

  const createTableMetricReportItems = (mapFn) =>
    computed(() =>
      metricReportRows.value.map((row) => {
        const { reportingPeriod, comparisonPeriod, change, percentageToGoal } =
          extractMetricData(row);
        const config = getFormatConfig({ value: reportingPeriod, disableAbbreviation: true });
        const dataValues = {
          reportingPeriod,
          comparisonPeriod,
          change,
          negativeMetric: negativeMetric.value,
          comparisonReporting: reportingPeriod,
          comparisonContext: comparisonPeriod,
          comparisonChange: change,
          ...getComparisonFormatConfig({
            reportingValue: reportingPeriod,
            comparisonValue: comparisonPeriod,
            isHover: true,
            disableAbbreviation: true,
          }),
          format: config.format,
          formatOptions: config.formatOptions,
          durationOptions: config.durationOptions,
        };
        row.subtitle = row?.follower_bucket ? row.follower_bucket : null;
        if (showGoalChip.value) {
          if (isArray(reportingPeriod)) {
            const chips = [];
            reportingPeriod.forEach((val, index) => {
              chips.push({
                ...getGoalChipProps(
                  reportMeta.value.goals.overall,
                  percentageToGoal[index],
                  metricTitle,
                  reportingPeriod[index],
                  config,
                  negativeMetric.value,
                ),
                variant: 'simple',
              });
            });
            dataValues.goalChip = chips;
          } else {
            dataValues.goalChip = {
              ...getGoalChipProps(
                reportMeta.value.goals.overall,
                percentageToGoal,
                metricTitle,
                reportingPeriod,
                config,
                negativeMetric.value,
              ),
              variant: 'simple',
            };
          }
        }
        return mapFn(row, dataValues);
      }),
    );

  const tableMetricReportItemsGroupedByCampaign = createTableMetricReportItems(
    (row, dataValues) => ({
      campaign: row.name || row.campaign,
      ...dataValues,
    }),
  );

  const tableMetricReportItemsGroupedByCountry = createTableMetricReportItems(
    (row, dataValues) => ({
      country: row.name || row.country,
      ...dataValues,
    }),
  );

  const tableMetricReportItemsGroupedByRegion = createTableMetricReportItems((row, dataValues) => ({
    region: row.name || row.region,
    ...dataValues,
  }));

  const tableMetricReportItemsGroupedByAgeGender = createTableMetricReportItems(
    (row, dataValues) => ({
      ageGender: row.name || row.age_gender,
      ...dataValues,
    }),
  );

  const tableMetricReportItemsGroupedByChannel = createTableMetricReportItems(
    (row, dataValues) => ({
      channel:
        (isTSIReportCategory.value ? row.channel : getDashboardChannelIconName(row.channel)) ||
        row.name,
      ...dataValues,
    }),
  );

  const doughnutMetricReportItemsRawData = computed(() => {
    const labels = metricReportRows.value.map((item) => item.chartLegend ?? item.name);
    const data = metricReportRows.value.map((item) => item.metric?.value);
    const total = data?.reduce((sum, value) => sum + value, 0);
    const percentageData = data?.map((value) => value / total);
    return {
      labels,
      percentageData,
      total,
    };
  });

  const doughnutMetricReportItems = computed(() => {
    const { labels, percentageData } = doughnutMetricReportItemsRawData.value;

    return {
      labels,
      legend: metricReportRows.value.map((item) => ({ avatar: item.avatar })),
      datasets: [
        {
          label: 'Total Social Impact (TSI)',
          data: percentageData,
        },
      ],
    };
  });

  const doughnutMetricReportTotal = computed(() => {
    const rawData = doughnutMetricReportItemsRawData.value;
    let total = rawData.total;
    if (visibleDatasetIndexes?.value && Array.isArray(visibleDatasetIndexes.value)) {
      const data = metricReportRows.value.map((item) => item.metric?.value || 0);
      total = visibleDatasetIndexes.value.reduce((sum, index) => sum + (data[index] || 0), 0);
    }
    return total;
  });

  const doughnutMetricReportTitle = computed(() =>
    Formatters.formatNumber(doughnutMetricReportTotal.value, Formatters.FORMAT_TYPES.ABBREVIATED, {
      formatOptions: {
        notation: doughnutMetricReportTotal.value >= 10_000_000_000 ? 'compact' : 'standard',
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: doughnutMetricReportTotal.value >= 10_000_000_000 ? 2 : 0,
      },
    }),
  );

  const doughnutMetricReportSubtitle = computed(() => {
    if (!reportMeta.value?.goals?.overall?.value) {
      return null;
    }
    const percent = Formatters.formatNumber(
      doughnutMetricReportTotal.value / reportMeta.value.goals.overall.value,
      Formatters.FORMAT_TYPES.PERCENT,
    );
    return `${percent} of Goal`;
  });

  const tableMetricReportItemsGroupedByGenericBreakdown = createTableMetricReportItems(
    (row, dataValues) => {
      if (!row.user_has_access) {
        row.avatar = null;
        row.error = true;
        if (aggregateBy.value === REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND.value)
          row.name = REPORT_ERROR_TOOLTIPS.NO_BRAND_ACCESS;
      }
      if (aggregateBy.value === REPORT_PREVIEW_AGGREGATE_BY_TYPES.CHANNEL.value) {
        return {
          channel: getDashboardChannelIconName(row?.name.toUpperCase()),
          ...dataValues,
        };
      }

      return {
        headerColumn: row,
        ...dataValues,
      };
    },
  );

  const tableMetricReportItemsGroupedByBrand = createTableMetricReportItems((row, dataValues) => {
    if (row?.user_has_access === false) {
      row.avatar = null;
      row.error = true;
      row.name = isCreatorReport.value
        ? REPORT_ERROR_TOOLTIPS.NO_CREATOR_ACCESS
        : REPORT_ERROR_TOOLTIPS.NO_BRAND_ACCESS;
    }
    if (isCreatorReport.value) {
      row.subtitle = `${abbrLargeNumber(row.followers, true, 0)} followers`;
      return {
        creator: row,
        ...dataValues,
      };
    }

    return {
      brand: row,
      ...dataValues,
    };
  });

  const tableMetricReportItemsGroupedByMedia = createTableMetricReportItems((row, dataValues) => ({
    ...dataValues,
    mediaType: row.mediaType?.tooltip,
  }));

  const tableMetricReportItemsCompetitive = createTableMetricReportItems((row, dataValues) => ({
    ...dataValues,
    brand: row,
  }));

  const tableMetricReportItemsBenchmark = createTableMetricReportItems((row, dataValues) => ({
    ...dataValues,
    benchmark: row,
  }));

  const extractTableMetricReportItems = computed(() => {
    if (reportType.value === REPORTS.MULTI_METRIC_MEDIA_TYPE.value) {
      return tableMetricReportItemsGroupedByMedia.value;
    }

    if (isCompetitiveReport.value) {
      return tableMetricReportItemsCompetitive.value;
    }

    if (isBenchmarkReport.value) {
      return tableMetricReportItemsBenchmark.value;
    }

    if (isCommunityReportCategory.value || isListeningReportCategory.value) {
      return tableMetricReportItemsGroupedByGenericBreakdown.value;
    }

    if (isAdsTotalGroupedMetricReport.value) {
      switch (aggregateBy.value) {
        case ORGANIC_TABLE_COLUMNS.BRAND:
          return tableMetricReportItemsGroupedByBrand.value;
        case ORGANIC_TABLE_COLUMNS.CHANNEL:
          return tableMetricReportItemsGroupedByChannel.value;
        case ADS_REPORT_AGGREGATE_BY_TYPES.COUNTRY.value:
          return tableMetricReportItemsGroupedByCountry.value;
        case ADS_REPORT_AGGREGATE_BY_TYPES.REGION.value:
          return tableMetricReportItemsGroupedByRegion.value;
        case ADS_REPORT_AGGREGATE_BY_TYPES.AGE_GENDER.value:
          return tableMetricReportItemsGroupedByAgeGender.value;
        case ADS_REPORT_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN.value:
          return tableMetricReportItemsGroupedByCampaign.value;
        case ORGANIC_TABLE_COLUMNS.MEDIA_TYPE:
          return tableMetricReportItemsGroupedByMedia.value;
        default:
          return tableMetricReportItemsGroupedByChannel.value;
      }
    }

    return aggregateBy.value === ORGANIC_TABLE_COLUMNS.BRAND
      ? tableMetricReportItemsGroupedByBrand.value
      : tableMetricReportItemsGroupedByChannel.value;
  });

  const errorMessageProp = computed(() => {
    if (serverErrorMessage.value) {
      return {
        variant: REPORT_ERROR_VARIANTS.SECTION_ERROR,
        title: REPORT_ERROR_MESSAGES.SERVER_ERROR,
      };
    }

    if (reportRequiresAdAccountButDisconnected.value) {
      return {
        variant: REPORT_ERROR_VARIANTS.SECTION_ERROR,
        title: errorMessageForDisplay.value,
      };
    }

    if (!hasNoData.value) return null;
    const startDate = new Date(reportDateRange[0]);
    const endDate = new Date(reportDateRange[1]);
    const metricValidFrom = targetMetricDetails.value?.metric_valid_from
      ? new Date(targetMetricDetails.value.metric_valid_from)
      : null;
    const metricValidUntil = targetMetricDetails.value?.metric_valid_until
      ? new Date(targetMetricDetails.value.metric_valid_until)
      : null;
    if (metricValidUntil && endDate > metricValidUntil) {
      const formattedDate = metricValidUntil.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return {
        variant: REPORT_ERROR_VARIANTS.SECTION_ERROR,
        title: TIME_PERIOD_MESSAGES.METRIC_NOT_AVAILABLE_AFTER_DATE(formattedDate),
      };
    }

    if (metricValidFrom && startDate < metricValidFrom) {
      const formattedDate = metricValidFrom.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return {
        variant: REPORT_ERROR_VARIANTS.SECTION_ERROR,
        title: TIME_PERIOD_MESSAGES.METRIC_NOT_AVAILABLE_BEFORE_DATE(formattedDate),
      };
    }

    const oldestReliableDate = new Date('2019-09-01');
    if (isUGCMetric.value && startDate < oldestReliableDate) {
      return {
        variant: REPORT_ERROR_VARIANTS.SECTION_ERROR,
        title: TIME_PERIOD_MESSAGES.UGC_TIME_PERIOD_EMPTY_STATE_MESSAGE,
      };
    }

    return {
      variant: hasErrors.value
        ? REPORT_ERROR_VARIANTS.SECTION_ERROR
        : REPORT_ERROR_VARIANTS.SECTION_EMPTY,
      title: errorMessageForDisplay.value,
    };
  });

  const singleMetricReportProps = computed(() => {
    if (loading.value && !serverErrorMessage.value) return null;

    if (errorMessageProp.value) {
      return {
        ...reportHeaderProps.value,
        error: errorMessageProp.value,
      };
    }

    const value =
      reportBrandsMetrics?.value?.[0]?.metric?.ALL_CHANNELS?.value ||
      reportBrandsMetrics?.value?.[0]?.metric?.value;
    const comparisonContextValue =
      reportBrandsMetrics?.value?.[0]?.metric?.ALL_CHANNELS?.context ||
      reportBrandsMetrics?.value?.[0]?.metric?.context;
    const comparisonChangeValue =
      reportBrandsMetrics?.value?.[0]?.metric?.ALL_CHANNELS?.context_change ||
      reportBrandsMetrics?.value?.[0]?.metric?.context_change;

    const formatConfig = getFormatConfig({ value, isSmallTile: true });
    const percentageToGoal = reportBrandsMetrics?.value?.[0]?.metric?.ALL_CHANNELS?.percent_of_goal;
    const props = {
      ...reportHeaderProps.value,
      value,
      currency: currency.value,
      comparisonChangeChip: {
        negativeMetric: negativeMetric.value,
        reporting: value,
        comparison: comparisonContextValue,
        change: comparisonChangeValue,
        ...getComparisonFormatConfig({
          reportingValue: value,
          comparisonValue: comparisonContextValue,
          isHover: true,
        }),
      },
      ...formatConfig,
      mediaType: reportMetricDetails.value.mediaTypeLabel
        ? {
            ...mediaTypeIcons.value,
            tooltip: mediaTypeTooltips.value,
          }
        : null,
    };

    if (showGoalChip.value)
      props.goalChip = getGoalChipProps(
        reportMeta.value.goals.overall,
        percentageToGoal,
        metricTitle,
        value,
        formatConfig,
        negativeMetric.value,
      );
    return props;
  });

  const reportPreviewProps = computed(() => ({
    ...reportHeaderProps.value,
    mediaType: reportMetricDetails.value.mediaTypeLabel
      ? {
          ...mediaTypeIcons.value,
          tooltip: mediaTypeTooltips.value,
        }
      : null,
  }));

  const contentReportProps = computed(() => {
    if (loading.value && !serverErrorMessage.value) return null;

    if (errorMessageProp.value) {
      return {
        ...reportHeaderProps.value,
        error: errorMessageProp.value,
      };
    }

    const props = {
      ...reportHeaderProps.value,
      mediaItems: formattedMediaItems.value,
      reportId: reportId.value,
    };
    return props;
  });

  const tableMetricReportProps = computed(() => {
    if (loading.value && !serverErrorMessage.value) return null;

    if (errorMessageProp.value) {
      return {
        ...reportHeaderProps.value,
        error: errorMessageProp.value,
      };
    }

    const props = {
      channelGroups: {
        channels: channels.value,
      },
      headerName: REPORT_PREVIEW_AGGREGATE_BY_TYPES?.[aggregateBy.value]?.formattedValue,
      pdfFormat: isPdfFormat.value,
      reportItems: extractTableMetricReportItems.value,
      ...reportHeaderProps.value,
      loading,
      mediaType:
        reportMetricDetails.value.mediaTypeLabel && !isMultiMediaTypeReport.value
          ? {
              ...mediaTypeIcons.value,
              tooltip: mediaTypeTooltips.value,
            }
          : null,
    };
    return props;
  });

  const graphReportChartData = computed(() => {
    if (isHorizontalBarChart.value) return prioritizedGraphReportData.value;
    return isDoughnutChart.value
      ? doughnutMetricReportItems.value
      : { datasets: prioritizedGraphReportData.value };
  });

  const graphReportProps = computed(() => {
    if (loading.value && !serverErrorMessage.value) return null;

    if (errorMessageProp.value) {
      return {
        ...reportHeaderProps.value,
        error: errorMessageProp.value,
      };
    }

    const props = {
      avatarGroups: avatarGroups.value,
      channelGroups: { channels: channels.value },
      metricTitle: metricTitle.value,
      chartType: graphType.value,
      isStacked: isStackedBarChart.value,
      chartData: graphReportChartData.value,
      title: isDoughnutChart.value ? doughnutMetricReportTitle.value : null,
      subtitle: isDoughnutChart.value ? doughnutMetricReportSubtitle.value : null,
      chartOptions: chartOptions.value,
      chartFormatConfig: chartFormatConfig.value,
      goalValues: goalValues.value,
      postType: postType.value,
      metricTimeFrame: timeframe.value,
      noData: prioritizedGraphReportData.value.length < 1,
      reportId: reportId.value,
      ...reportHeaderProps.value,
      mediaType:
        reportMetricDetails.value.mediaTypeLabel && !(reportMeta.value.metrics.length > 1)
          ? {
              ...mediaTypeIcons.value,
              tooltip: mediaTypeTooltips.value,
            }
          : null,
      showLegend: showLegend.value,
    };
    if (isHorizontalBarChart.value) {
      props.showLegend = showLegend.value;
    }
    return props;
  });

  const noteReportProps = computed(() => {
    if (loading.value && !serverErrorMessage.value) return null;
    return {
      content: report.value?.meta?.text,
      reportId: reportId.value,
      isPdfFormat: isPdfFormat.value,
      showMenu: !isPdfFormat.value && !isShared.value,
    };
  });

  const headerReportProps = computed(() => ({
    report: report.value,
    type: reportType.value,
    loading: loading.value,
    preview,
    showMenu: !isPdfFormat.value && !isShared.value,
  }));

  return {
    getBrandErrorIcons,
    contentReportProps,
    contentTagIds,
    contentTagNames,
    dateRangeDisplay,
    errorMessageProp,
    extractTableMetricReportItems,
    formattedMediaItems,
    graphReportProps,
    headerReportProps,
    isPdfFormat,
    isUGCMetric,
    mediaTypeTooltips,
    metricTitle,
    noteReportProps,
    reportBrands,
    reportAttributes,
    reportPreviewProps,
    reportHeaderProps,
    singleMetricReportProps,
    tableMetricReportProps,
    generateCustomTooltip,
    doughnutMetricReportItems,
    doughnutMetricReportTitle,
    extractMetricData,
    doughnutMetricReportSubtitle,
  };
}
